import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, TextField, Typography } from "@material-ui/core";
import { Autocomplete } from "@mui/material";
import { Add, Description } from "@material-ui/icons";

import CardTable from "components/CardTable/CardTable";
import { TableInputs } from "components/CardTable/TableInputs";
import { ModalV2 } from "components/Modal/ModalV2";
import RegularButton from "components/CustomButtons/Button";
import { useToggle } from "hooks/useToggle";
import { useSearch } from "hooks/useSearch";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";

import {
  getRetentionOutputs,
  setRetentionOutputsFilters,
  updateRetentionOutputStatus,
  deleteRetentionOutput,
  downloadEvidence,
} from "redux/actions/retentionOutputs/retentionOutputsActions";
import { columns } from "./columns";
import { RetentionOutputForm } from "./RetentionOutputForm";
import { EvidenceUploadModal } from "./EvidenceUploadModal";
import { RETENTION_OUTPUTS_LABELS } from "./labels";
import { monthsDictionary } from "helpers/dictionary";
import { retentionsConfirmMessages } from "helpers/retentionsDictionary";

const { title, filters, messages } = RETENTION_OUTPUTS_LABELS;

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

export default function RetentionOutputsView() {
  const dispatch = useDispatch();
  const { setSnackbar } = useSearch();
  const [open, setOpen] = useToggle();
  const [confirmModal, setConfirmModal] = useState({ open: false, row: null, status: "" });
  const [evidenceModal, setEvidenceModal] = useState({ open: false, row: null });

  const { retentionOutputs, station, month, year } = useSelector(
    (state) => state.retentionOutputs
  );
  const { isLoadingComp } = useSelector(({ search }) => search);
  const canCreate = useRuleValidator(PERMISSIONS.WITHHOLDINGS_CREATE);

  const openConfirm = (row, status) => setConfirmModal({ open: true, row, status });
  const closeConfirm = () => setConfirmModal({ open: false, row: null, status: "" });

  const handleConfirm = () => {
    const { row, status } = confirmModal;
    if (status === "deleted") {
      dispatch(deleteRetentionOutput(row.id, closeConfirm));
    } else {
      dispatch(updateRetentionOutputStatus(row.id, status, closeConfirm));
    }
  };

  const handleAccept = (row) => openConfirm(row, "authorized");
  const handleReject = (row) => openConfirm(row, "rejected");
  const handleDeliver = (row) => openConfirm(row, "delivered");
  const handleDelete = (row) => openConfirm(row, "deleted");

  const handleUploadEvidence = (row) => setEvidenceModal({ open: true, row });
  const closeEvidenceModal = () => setEvidenceModal({ open: false, row: null });
  const handleViewEvidence = (row) => dispatch(downloadEvidence(row.id));

  const handleMonthChange = (_, value) => {
    dispatch(setRetentionOutputsFilters("month", value?.value || null));
  };

  const handleYearChange = (_, value) => {
    dispatch(setRetentionOutputsFilters("year", value));
  };

  const handleSubmit = (e) => {
    if (!station?.id) {
      setSnackbar(messages.selectStation, "warning");
      return;
    }
    dispatch(getRetentionOutputs(station, month, year));
  };

  const selectedMonth = monthsDictionary.find((m) => m.value === month) || null;

  return (
    <>
      <CardTable
        data={retentionOutputs}
        columns={columns({
          onAccept: handleAccept,
          onReject: handleReject,
          onDeliver: handleDeliver,
          onDelete: handleDelete,
          onUploadEvidence: handleUploadEvidence,
          onViewEvidence: handleViewEvidence,
        })}
        hasHeader
        blockBtn
        title={title}
        icon={<Description />}
        isLoading={isLoadingComp?.loadingRetentionOutputs}
        tableContent={
          <TableInputs
            stations
            reducer="retentionOutputs"
            dispatchAction={setRetentionOutputsFilters}
            submitAction={handleSubmit}
            loading={isLoadingComp?.loadingRetentionOutputs}
          >
            <Grid item xs={12} sm={2}>
              <Autocomplete
                options={monthsDictionary}
                getOptionLabel={(option) => option.label}
                value={selectedMonth}
                onChange={handleMonthChange}
                renderInput={(params) => (
                  <TextField {...params} label={filters.month} variant="outlined" size="small" />
                )}
                noOptionsText={filters.noOptions}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Autocomplete
                options={yearOptions}
                getOptionLabel={(option) => String(option)}
                value={year}
                onChange={handleYearChange}
                disableClearable
                renderInput={(params) => (
                  <TextField {...params} label={filters.year} variant="outlined" size="small" />
                )}
                noOptionsText={filters.noOptions}
              />
            </Grid>
          </TableInputs>
        }
        hasControl
        controls={
          canCreate && (
            <RegularButton round justIcon color="info" onClick={setOpen}>
              <Add style={{ margin: 0 }} />
            </RegularButton>
          )
        }
      />

      <RetentionOutputForm open={open} onClose={setOpen} />

      <ModalV2
        open={confirmModal.open}
        onClose={closeConfirm}
        justConfirm
        title="Confirmación"
        onSubmit={handleConfirm}
        confirmText="Confirmar"
        confirmColor="success"
        loading={isLoadingComp?.updatingRetentionOutput}
      >
        <Grid item xs={12}>
          <Typography align="center">
            {retentionsConfirmMessages[confirmModal.status]}
          </Typography>
        </Grid>
      </ModalV2>

      <EvidenceUploadModal
        open={evidenceModal.open}
        onClose={closeEvidenceModal}
        row={evidenceModal.row}
      />
    </>
  );
}
